import React, { useState } from 'react';
import './BildeOpplaster.css';

/**
 * Mobilvenlig bildeopplaster med kamera-støtte
 *
 * Props:
 * - type: 'eiendom' | 'bygning' | 'bygningsdel' | 'rom'
 * - id: ID til entitet
 * - sporsmalId: (valgfritt) For spørsmål-spesifikke bilder
 * - bildeposisjon: 'generelt' | 'sporsmal' (default: 'generelt')
 * - placeholder: Tekst for bildeopplasting-hint
 * - onUploadSuccess: Callback når bilde er lastet opp
 */
function BildeOpplaster({
  type,
  id,
  sporsmalId = null,
  bildeposisjon = 'generelt',
  placeholder = 'Ta bilde',
  onUploadSuccess
}) {
  const [bildetekst, setBildetekst] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  // Detekter mobil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleBildeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validering
    if (!file.type.startsWith('image/')) {
      setError('Kun bildefiler er tillatt');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB
      setError('Bildet er for stort. Maksimal størrelse er 10MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev';

      // Bygg endpoint basert på type
      let endpoint;
      if (type === 'eiendom') {
        endpoint = `${API_URL}/api/eiendom/${id}/bilder`;
      } else if (type === 'bygning') {
        endpoint = `${API_URL}/api/bygning/${id}/bilder`;
      } else if (type === 'bygningsdel') {
        endpoint = `${API_URL}/api/bygningsdel/id/${id}/bilder`;
      } else if (type === 'rom') {
        endpoint = `${API_URL}/api/rom/${id}/bilder`;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('bildetekst', bildetekst);
      formData.append('bildeposisjon', bildeposisjon);

      if (sporsmalId) {
        formData.append('sporsmal_id', sporsmalId.toString());
      }

      // Hent token fra localStorage
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: headers,
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Kunne ikke laste opp bilde');
      }

      // Reset form
      setBildetekst('');
      event.target.value = '';

      // Callback
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (err) {
      setError('Feil ved opplasting: ' + err.message);
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bilde-opplaster">
      {isMobile ? (
        // MOBIL: Stor knapp med kamera-ikon
        <label className="kamera-knapp-mobil">
          📸 {placeholder}
          <input
            type="file"
            accept="image/*"
            capture="environment"  // Bakre kamera
            onChange={handleBildeUpload}
            disabled={uploading}
            style={{display: 'none'}}
          />
        </label>
      ) : (
        // DESKTOP: Standard file picker
        <div className="bilde-dropzone">
          <label className="file-label">
            <input
              type="file"
              accept="image/*"
              onChange={handleBildeUpload}
              disabled={uploading}
              style={{display: 'none'}}
            />
            <div className="upload-prompt">
              📁 {placeholder} (eller dra og slipp)
            </div>
          </label>
        </div>
      )}

      {/* Bildetekst-input */}
      <div className="bildetekst-container">
        <label className="bildetekst-label" style={{
          display: 'block',
          marginBottom: '5px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: '#424242'
        }}>
          📝 Beskrivelse av bildet (vises i rapporten)
        </label>
        <p style={{
          margin: '0 0 8px 0',
          fontSize: '12px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          Beskriv HVA som vises i bildet (ikke gjenta spørsmålet)
        </p>
        <textarea
          value={bildetekst}
          onChange={(e) => setBildetekst(e.target.value)}
          placeholder="F.eks: Fasade nord med sprekkdannelse i mønestein, Takstein med løs teglstein ved skorstein, Vannlekkasje ved vindu i 2. etasje"
          className="bildetekst-input"
          disabled={uploading}
          rows={3}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontFamily: 'inherit',
            fontSize: '13px',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Loading state */}
      {uploading && (
        <div className="upload-status">
          ⏳ Laster opp bilde...
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="upload-error">
          ❌ {error}
        </div>
      )}
    </div>
  );
}

export default BildeOpplaster;
